const React = require('react');
const FieldContext = require('./FieldContext');

const internals = {};
const DEFAULT_FIELD_LABEL = 'Form field';
const DEFAULT_LABEL_COLOR = '#12274f';

internals.getTextLabel = (label) => {

    if (typeof label === 'string') {
        return label;
    }

    if (typeof label === 'number') {
        return String(label);
    }

    return '';
};

internals.isReactComponentObject = (Component) => !!Component
    && typeof Component === 'object'
    && !!Component.$$typeof;

internals.isRenderableComponent = (Component) => typeof Component === 'function'
    || typeof Component === 'string'
    || internals.isReactComponentObject(Component);

internals.getOwnExportValues = (Component) => {

    if (!Component || typeof Component !== 'object') {
        return [];
    }

    return [
        ...Object.getOwnPropertyNames(Component),
        ...Object.getOwnPropertySymbols(Component)
    ].map((key) => Component[key]);
};

internals.unwrapComponent = (Component) => {

    if (!Component || internals.isRenderableComponent(Component)) {
        return Component;
    }

    if (internals.isRenderableComponent(Component.default)) {
        return Component.default;
    }

    const renderableExport = internals.getOwnExportValues(Component).find(internals.isRenderableComponent);

    return renderableExport || Component;
};

internals.getComponentDiagnostic = (Component) => {

    if (!Component) {
        return String(Component);
    }

    if (typeof Component !== 'object') {
        return typeof Component;
    }

    const keys = Object.keys(Component);
    const ownNames = Object.getOwnPropertyNames(Component);
    const ownSymbols = Object.getOwnPropertySymbols(Component).map(String);

    return [
        `object keys: ${keys.join(', ') || '(none)'}`,
        `own names: ${ownNames.join(', ') || '(none)'}`,
        `own symbols: ${ownSymbols.join(', ') || '(none)'}`
    ].join('; ');
};

internals.assertRenderableComponent = ({
    Component,
    fieldName,
    field
}) => {

    if (internals.isRenderableComponent(Component)) {
        return;
    }

    throw new Error([
        'Could not render form field component.',
        `fieldName=${fieldName || field?.name || '(unknown)'}`,
        `fieldType=${field?.type || '(unknown)'}`,
        `component=${internals.getComponentDiagnostic(Component)}`
    ].join(' '));
};

internals.getAccessibleFieldLabel = (field = {}) => internals.getTextLabel(field.ariaLabel)
    || internals.getTextLabel(field['aria-label'])
    || internals.getTextLabel(field.accessibleLabel)
    || internals.getTextLabel(field.label)
    || internals.getTextLabel(field.definition?.label)
    || field.name
    || DEFAULT_FIELD_LABEL;

internals.hasVisibleFieldLabel = (field = {}) => typeof field.label !== 'undefined'
    && field.label !== null
    && field.label !== '';

internals.getFieldDomId = (field = {}) => field.id || field.inputId || field.name;

internals.getFieldLabelId = (field = {}) => field.labelId
    || (field.name && internals.hasVisibleFieldLabel(field) ? `${field.name}-label` : undefined);

internals.buildInputProps = ({
    field = {},
    includeId = false,
    inputProps = {}
} = {}) => {

    const nextInputProps = {
        ...inputProps
    };
    const id = internals.getFieldDomId(field);
    const accessibleLabel = internals.getAccessibleFieldLabel(field);

    if (includeId && id && !nextInputProps.id) {
        nextInputProps.id = id;
    }

    if (field.name && !nextInputProps.name) {
        nextInputProps.name = field.name;
    }

    if (!nextInputProps['aria-label']) {
        nextInputProps['aria-label'] = accessibleLabel;
    }

    if (!nextInputProps.title) {
        nextInputProps.title = accessibleLabel;
    }

    return nextInputProps;
};

internals.mergeLabelSx = (sx) => ({
    ...(sx || {}),
    color: sx?.color || DEFAULT_LABEL_COLOR,
    '&.Mui-focused': {
        ...(sx?.['&.Mui-focused'] || {}),
        color: sx?.['&.Mui-focused']?.color || DEFAULT_LABEL_COLOR
    },
    '&.Mui-disabled': {
        ...(sx?.['&.Mui-disabled'] || {}),
        color: sx?.['&.Mui-disabled']?.color || DEFAULT_LABEL_COLOR
    }
});

internals.buildInputLabelProps = ({
    inputLabelProps = {},
    labelId
} = {}) => ({
    ...(labelId ? { id: labelId } : {}),
    ...inputLabelProps,
    sx: internals.mergeLabelSx(inputLabelProps.sx)
});

internals.buildSelectProps = ({
    field = {},
    inputProps = {},
    labelId,
    selectProps = {}
} = {}) => {

    const selectLabelId = selectProps.labelId || labelId;

    return {
        ...selectProps,
        ...(selectLabelId ? { labelId: selectLabelId } : {}),
        inputProps: internals.buildInputProps({
            field,
            inputProps: {
                ...inputProps,
                ...(selectProps.inputProps || {})
            }
        })
    };
};

internals.buildControlProps = ({
    controlProps = {},
    field = {},
    inputProps = {}
} = {}) => ({
    ...controlProps,
    inputProps: internals.buildInputProps({
        field,
        inputProps: {
            ...inputProps,
            ...(controlProps.inputProps || {})
        }
    })
});

internals.buildTextFieldProps = ({
    field = {},
    textFieldProps = {}
} = {}) => {

    const labelId = textFieldProps.InputLabelProps?.id || internals.getFieldLabelId(field);
    const id = textFieldProps.id || internals.getFieldDomId(field);
    const inputProps = internals.buildInputProps({
        field,
        inputProps: textFieldProps.inputProps
    });

    return {
        ...(id ? { id } : {}),
        ...(field.name ? { name: field.name } : {}),
        ...textFieldProps,
        InputLabelProps: internals.buildInputLabelProps({
            inputLabelProps: textFieldProps.InputLabelProps,
            labelId
        }),
        inputProps,
        SelectProps: internals.buildSelectProps({
            field,
            inputProps,
            labelId,
            selectProps: textFieldProps.SelectProps
        })
    };
};

internals.buildAccessibleField = ({
    field = {},
    labelId,
    textFieldInputProps = {}
} = {}) => ({
    ...field,
    inputProps: internals.buildInputProps({
        field,
        inputProps: {
            ...textFieldInputProps,
            ...(field.inputProps || {})
        }
    }),
    checkboxProps: internals.buildControlProps({
        controlProps: field.checkboxProps,
        field,
        inputProps: textFieldInputProps
    }),
    switchProps: internals.buildControlProps({
        controlProps: field.switchProps,
        field,
        inputProps: textFieldInputProps
    }),
    selectProps: internals.buildSelectProps({
        field,
        inputProps: textFieldInputProps,
        labelId,
        selectProps: field.selectProps
    })
});

module.exports = function ControlledFieldAdapter(props) {

    const {
        Component,
        field,
        fieldName,
        onBlur,
        onChange,
        onFocus,
        textFieldProps = {},
        useField: requestedUseField,
        ...componentProps
    } = props;

    const ResolvedComponent = internals.unwrapComponent(Component);

    const resolvedUseField = typeof requestedUseField === 'function'
        ? requestedUseField
        : FieldContext.useField;
    const fieldController = resolvedUseField(fieldName);
    const resolvedField = field || fieldController.definition;

    internals.assertRenderableComponent({
        Component: ResolvedComponent,
        fieldName,
        field: resolvedField
    });

    const resolvedTextFieldProps = internals.buildTextFieldProps({
        field: resolvedField,
        textFieldProps: fieldController.getTextFieldProps?.(textFieldProps) || textFieldProps
    });
    const resolvedLabelId = resolvedTextFieldProps.InputLabelProps?.id || internals.getFieldLabelId(resolvedField);
    const accessibleField = internals.buildAccessibleField({
        field: resolvedField,
        labelId: resolvedLabelId,
        textFieldInputProps: resolvedTextFieldProps.inputProps
    });
    const accessibleComponentProps = {
        ...componentProps,
        checkboxProps: internals.buildControlProps({
            controlProps: {
                ...accessibleField.checkboxProps,
                ...componentProps.checkboxProps
            },
            field: resolvedField,
            inputProps: resolvedTextFieldProps.inputProps
        }),
        selectProps: internals.buildSelectProps({
            field: resolvedField,
            inputProps: resolvedTextFieldProps.inputProps,
            labelId: resolvedLabelId,
            selectProps: {
                ...accessibleField.selectProps,
                ...componentProps.selectProps
            }
        }),
        switchProps: internals.buildControlProps({
            controlProps: {
                ...accessibleField.switchProps,
                ...componentProps.switchProps
            },
            field: resolvedField,
            inputProps: resolvedTextFieldProps.inputProps
        })
    };

    return <ResolvedComponent
        {...accessibleComponentProps}
        field={accessibleField}
        error={!!resolvedTextFieldProps.error}
        helperText={resolvedTextFieldProps.helperText}
        textFieldProps={resolvedTextFieldProps}
        formProps={fieldController.formProps}
        value={fieldController.getValue()}
        setValue={(value, eventOrOptions, maybeEvent) => {

            const isEventLike = !!eventOrOptions && typeof eventOrOptions === 'object' && (
                typeof eventOrOptions.preventDefault === 'function'
                || typeof eventOrOptions.stopPropagation === 'function'
                || !!eventOrOptions.target
            );

            const options = isEventLike ? undefined : eventOrOptions;
            const event = isEventLike ? eventOrOptions : maybeEvent;

            return fieldController.setValue(value, options, event);
        }}
        onBlur={(event) => {

            fieldController.onBlur?.(event);

            if (typeof onBlur === 'function') {
                onBlur(event);
            }
        }}
        onChange={onChange}
        onFocus={(event) => {

            fieldController.onFocus?.(event);

            if (typeof onFocus === 'function') {
                onFocus(event);
            }
        }}
    />;
};

module.exports.internals = internals;
